import React, { useState, useEffect } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Terms Data                                                                  */
/* ─────────────────────────────────────────────────────────────────────────── */

interface TermsSection {
  id: string;
  title: string;
  icon: string;
  content: string[];
}

const TERMS_SECTIONS: TermsSection[] = [
  {
    id: 'general',
    title: 'General Terms of Use',
    icon: 'zmdi-file-text',
    content: [
      'By accessing and using this website, you accept and agree to be bound by the terms and conditions set out below. If you do not agree to these terms, please do not use our website or services.',
      'These terms apply to all visitors, customers, and users of our website and services. We reserve the right to update or modify these terms at any time without prior notice. Your continued use of the website constitutes acceptance of any changes.',
      'All content on this website, including text, images, product descriptions, and pricing, is provided for general information purposes. While we strive for accuracy, we do not guarantee that all information is completely up to date at all times.',
      'You must be at least 18 years of age to place an order on our website. By placing an order, you confirm that you meet this age requirement.',
      'Unauthorised use of this website may give rise to a claim for damages and/or constitute a criminal offence. You agree not to use the site for any unlawful or prohibited purpose.',
    ],
  },
  {
    id: 'products',
    title: 'Vehicles & Pricing',
    icon: 'zmdi-car',
    content: [
      'Every vehicle listing includes mileage, condition notes, and service history to the best of our knowledge at the time of listing. Minor cosmetic wear consistent with a car\'s age and mileage is normal and does not constitute a defect.',
      'The Vehicle Identification Number (VIN), registration details, and inspection report are available on request for any car in our inventory.',
      'Listing images are as accurate as possible; however, colour rendering may vary depending on your display settings and lighting conditions. We recommend viewing the vehicle in person before finalising a purchase.',
      'Prices are displayed in the currency shown on the website and are inclusive of applicable taxes unless otherwise stated. Prices are subject to change without notice, reflecting market conditions.',
      'We reserve the right to mark a vehicle as sold or unavailable at any time. Promotional prices and offers are valid only for the stated period and while the vehicle remains in our inventory.',
      'Trade-in and financing arrangements are quoted individually based on the vehicle and applicant. A written quotation will be provided before any agreement is finalised, and a deposit is required to secure a reservation.',
    ],
  },
  {
    id: 'orders',
    title: 'Reservations & Contract',
    icon: 'zmdi-car-taxi',
    content: [
      'Submitting a reservation, purchase, or test drive request does not constitute a binding contract. A contract is formed only once you and our sales team agree on final terms and a deposit or payment has been accepted.',
      'We reserve the right to decline or cancel any reservation at our sole discretion, including requests that appear fraudulent, requests with incorrect pricing due to technical errors, or requests for a vehicle that has already been sold.',
      'If we are unable to fulfil your purchase after a deposit has been received — for example, if the vehicle is no longer available — we will notify you promptly and arrange a full refund of your deposit.',
      'You are responsible for ensuring that all details provided — including your contact information — are accurate. We cannot be held liable for missed communications resulting from incorrect information.',
      'For high-value purchases exceeding a threshold determined at our discretion, we may request additional identity verification before finalising the sale.',
    ],
  },
  {
    id: 'payments',
    title: 'Payments & Security',
    icon: 'zmdi-card',
    content: [
      'All payments are processed through secure, PCI-DSS compliant payment gateways. We accept major credit and debit cards, PayPal, and bank transfers. Cash on delivery may be available in select areas.',
      'We do not store your card details on our servers at any point. All financial transactions are encrypted using SSL technology.',
      'In the event of a chargeback or payment dispute raised without first contacting our support team, we reserve the right to suspend your account pending investigation.',
      'For bank transfers, your order will only be processed once cleared funds have been received. Please allow 2–3 business days for bank transfer payments to clear.',
      'Prices quoted in foreign currencies are for reference only. Your payment provider will apply their exchange rate at the time of the transaction.',
    ],
  },
  {
    id: 'shipping',
    title: 'Delivery & Pickup',
    icon: 'zmdi-truck',
    content: [
      'Vehicles ready for delivery are prepared and transported with care. We arrange transport via licensed carriers, or you may collect your vehicle directly from our dealership.',
      'Delivery timeframes are estimates and may be affected by registration processing, public holidays, and carrier delays beyond our control. We will not be liable for delays outside our reasonable control.',
      'Delivery outside our standard service area may be subject to additional transport fees. These charges will be confirmed with you before dispatch and are not included in the vehicle price.',
      'Risk of loss for a vehicle passes to you upon delivery or pickup. If a vehicle arrives with visible transport damage, please note it with the carrier at the time of delivery and contact us immediately.',
      'A physical delivery address and a valid ID with signature upon receipt will be required for all deliveries.',
    ],
  },
  {
    id: 'returns',
    title: 'Deposits & Cancellations',
    icon: 'zmdi-refresh-alt',
    content: [
      'Reservation deposits are refundable if a sale is cancelled before it is finalised, provided the request is made in accordance with the reservation terms agreed at the time of booking.',
      'Vehicle modifications or add-ons arranged at the buyer\'s request are non-refundable once work has begun, unless they are faulty or not as described.',
      'To request a cancellation or refund, please contact our sales team. Cancellations made without prior contact may take longer to process.',
      'Refunds are processed within 5–10 business days of a cancellation being approved. Refunds will be issued via the original payment method.',
      'We do not refund transport or delivery charges already incurred unless the cancellation is due to our error or a defect in the vehicle.',
      'If a vehicle is found to have been damaged or misrepresented after a sale is finalised, we reserve the right to investigate before agreeing to any refund or exchange.',
    ],
  },
  {
    id: 'warranty',
    title: 'Warranty & Vehicle History',
    icon: 'zmdi-badge-check',
    content: [
      'Every vehicle we sell is accompanied by a vehicle history and inspection summary confirming mileage, prior accidents (if known), and available service records.',
      'We offer a limited warranty period, specified at the time of sale, against undisclosed mechanical defects present at the time of purchase. This warranty does not cover damage resulting from normal wear and tear, misuse, accidents after purchase, or unauthorised repairs.',
      'Warranty claims must be accompanied by proof of purchase. We reserve the right to repair, replace parts, or offer compensation at our discretion upon verification of the defect.',
      'We recommend regular servicing and maintenance to keep your vehicle in good condition. Damage resulting from neglect or lack of maintenance is not covered by warranty.',
    ],
  },
  {
    id: 'ip',
    title: 'Intellectual Property',
    icon: 'zmdi-copyright',
    content: [
      'All content on this website — including but not limited to designs, photographs, text, logos, and graphics — is our intellectual property or that of our licensors and is protected by copyright and other applicable laws.',
      'You may not reproduce, distribute, display, or create derivative works from any content on this site without our express written permission.',
      'Our trade name, logo, and all product designs are trademarks of our business. Unauthorised use of our trademarks is strictly prohibited.',
      'If you believe any content on our website infringes your intellectual property rights, please contact us with full details and we will investigate promptly.',
    ],
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    icon: 'zmdi-alert-triangle',
    content: [
      'To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or products.',
      'Our total liability in connection with any order shall not exceed the amount paid by you for that specific order.',
      'We make no warranty that the website will be uninterrupted, error-free, or free of viruses or other harmful components.',
      'These terms do not exclude or limit our liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded by applicable law.',
    ],
  },
  {
    id: 'governing',
    title: 'Governing Law',
    icon: 'zmdi-balance',
    content: [
      'These terms and conditions are governed by and construed in accordance with the laws of the jurisdiction in which our business is registered.',
      'Any disputes arising under or in connection with these terms shall be subject to the exclusive jurisdiction of the courts of that jurisdiction.',
      'If any provision of these terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.',
      'These terms constitute the entire agreement between you and us in relation to your use of this website and supersede all previous agreements.',
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main Page                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

const TermsAndConditions: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>(TERMS_SECTIONS[0].id);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      for (const section of TERMS_SECTIONS) {
        const el = document.getElementById(`tc-section-${section.id}`);
        if (el && el.offsetTop <= scrollY) {
          setActiveSection(section.id);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(`tc-section-${id}`);
    if (el) {
      const offset = 90;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div>
      <Header />
      <HeaderSpacer />

      {/* Breadcrumb */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">Terms & Conditions</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* ── Hero ── */}
          <div className="lp-hero">
            <div className="lp-hero__icon">
              <i className="zmdi zmdi-file-text"></i>
            </div>
            <h2 className="mtext-111 cl2 p-b-10">Terms &amp; Conditions</h2>
            <p className="stext-113 cl6 lp-hero__sub">
              Please read these terms carefully before purchasing from our store. By placing an order, you agree to the conditions outlined below.
            </p>
            <p className="stext-113 cl6" style={{ fontSize: 12, marginTop: 6 }}>
              Last updated: June 2025 &nbsp;·&nbsp; {TERMS_SECTIONS.length} sections
            </p>
          </div>

          {/* ── Layout ── */}
          <div className="lp-layout">

            {/* Sticky sidebar nav */}
            <nav className="lp-nav">
              <p className="lp-nav__heading">Contents</p>
              {TERMS_SECTIONS.map((sec, i) => (
                <button
                  key={sec.id}
                  className={`lp-nav__item ${activeSection === sec.id ? 'lp-nav__item--active' : ''}`}
                  onClick={() => scrollTo(sec.id)}
                  type="button"
                >
                  <span className="lp-nav__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="lp-nav__label">{sec.title}</span>
                </button>
              ))}
            </nav>

            {/* Content */}
            <div className="lp-content">
              {TERMS_SECTIONS.map((sec, i) => (
                <div
                  key={sec.id}
                  id={`tc-section-${sec.id}`}
                  className="lp-section"
                >
                  <div className="lp-section__header">
                    <div className="lp-section__num">{String(i + 1).padStart(2, '0')}</div>
                    <div className="lp-section__title-wrap">
                      <i className={`zmdi ${sec.icon} lp-section__icon`}></i>
                      <h4 className="lp-section__title">{sec.title}</h4>
                    </div>
                  </div>
                  <div className="lp-section__body">
                    {sec.content.map((para, j) => (
                      <p key={j} className="lp-section__para">{para}</p>
                    ))}
                  </div>
                </div>
              ))}

              {/* Footer note */}
              <div className="lp-footnote">
                <i className="zmdi zmdi-info-outline" style={{ fontSize: 20, color: '#717fe0', marginBottom: 10, display: 'block' }}></i>
                <p className="stext-113 cl6">
                  If you have any questions about these Terms &amp; Conditions, please{' '}
                  <a href="/contact" className="lp-link">contact our team</a>. We are happy to clarify anything before you place your order.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Hero ── */
        .lp-hero {
          text-align: center;
          padding-bottom: 56px;
        }
        .lp-hero__icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #f4f4f9;
          border: 1px solid #e0e0f8;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 28px;
          color: #717fe0;
        }
        .lp-hero__sub {
          max-width: 540px;
          margin: 0 auto 8px;
          line-height: 1.7;
        }

        /* ── Layout ── */
        .lp-layout {
          display: grid;
          grid-template-columns: 230px 1fr;
          gap: 48px;
          align-items: start;
        }

        /* ── Sidebar nav ── */
        .lp-nav {
          position: sticky;
          top: 24px;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .lp-nav__heading {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          color: #bbb;
          margin-bottom: 10px;
          padding-left: 12px;
        }
        .lp-nav__item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          border: none;
          border-radius: 5px;
          background: none;
          cursor: pointer;
          text-align: left;
          width: 100%;
          font-family: Poppins-Regular, sans-serif;
          transition: background 0.2s;
          border-left: 2px solid transparent;
        }
        .lp-nav__item:hover {
          background: #f5f5ff;
        }
        .lp-nav__item--active {
          background: #f0f2ff;
          border-left-color: #717fe0;
        }
        .lp-nav__num {
          font-size: 10px;
          font-weight: 700;
          color: #ccc;
          flex-shrink: 0;
          font-variant-numeric: tabular-nums;
          transition: color 0.2s;
        }
        .lp-nav__item--active .lp-nav__num { color: #717fe0; }
        .lp-nav__label {
          font-size: 12px;
          color: #777;
          line-height: 1.4;
          transition: color 0.2s;
        }
        .lp-nav__item--active .lp-nav__label { color: #333; font-weight: 500; }

        /* ── Content sections ── */
        .lp-section {
          margin-bottom: 52px;
          scroll-margin-top: 90px;
        }
        .lp-section__header {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e0e0f8;
        }
        .lp-section__num {
          font-size: 12px;
          font-weight: 700;
          color: #717fe0;
          letter-spacing: 0.5px;
          line-height: 1;
          margin-top: 3px;
          flex-shrink: 0;
          font-variant-numeric: tabular-nums;
        }
        .lp-section__title-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .lp-section__icon {
          font-size: 18px;
          color: #717fe0;
        }
        .lp-section__title {
          font-size: 16px;
          font-weight: 600;
          color: #2c2c2c;
          margin: 0;
        }
        .lp-section__body {
          padding-left: 0;
        }
        .lp-section__para {
          font-size: 13.5px;
          color: #555;
          line-height: 1.85;
          margin: 0 0 14px;
          padding-left: 26px;
          position: relative;
        }
        .lp-section__para::before {
          content: '';
          position: absolute;
          left: 10px;
          top: 10px;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #717fe0;
        }
        .lp-section__para:last-child { margin-bottom: 0; }

        /* ── Footnote ── */
        .lp-footnote {
          background: #f8f9ff;
          border: 1px solid #dde2f8;
          border-radius: 8px;
          padding: 28px 28px;
          text-align: center;
          margin-top: 16px;
        }
        .lp-link {
          color: #717fe0;
          text-decoration: underline;
          text-underline-offset: 2px;
          transition: color 0.2s;
        }
        .lp-link:hover { color: #4a55c8; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .lp-layout {
            grid-template-columns: 1fr;
          }
          .lp-nav {
            position: static;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 4px;
          }
          .lp-nav__heading { display: none; }
          .lp-nav__item {
            flex: 0 0 auto;
            padding: 6px 12px;
            border-left: none;
            border-bottom: 2px solid transparent;
          }
          .lp-nav__item--active {
            border-left-color: transparent;
            border-bottom-color: #717fe0;
          }
          .lp-nav__num { display: none; }
          .lp-nav__label { font-size: 11px; }
        }
        @media (max-width: 480px) {
          .lp-section__para {
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsAndConditions;